import React from 'react';
import { Status } from '../types';

/**
 * Props for the StatusBadge component.
 */
interface StatusBadgeProps {
  status: Status;
}

/**
 * A component that displays a colored badge representing the status of a maintenance task.
 * @param {StatusBadgeProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered status badge.
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  // A mapping from status enum to corresponding Tailwind CSS classes for styling.
  const statusStyles: { [key in Status]: { bg: string; text: string; dot: string } } = {
    [Status.Open]: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
    [Status.InProgress]: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
    [Status.Completed]: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
    [Status.Pending]: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  };

  // Default style for any unknown or unhandled statuses.
  const defaultStyle = { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500' };
  const styles = statusStyles[status] || defaultStyle;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${styles.bg} ${styles.text}`}>
      <span className={`w-2 h-2 mr-2 rounded-full ${styles.dot}`}></span>
      {/* Capitalize the first letter of the status for display */}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

export default StatusBadge;
